import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

import { useAuth } from '../../../contexts/AuthContext';
import { fetchWithAuth, isAdminUser } from '../../../utils/api';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Badge } from '../../ui/badge';

interface SettingsNavItem {
  name: string;
  path: string;
  adminOnly?: boolean;
}

interface SettingsNavSection {
  title: string;
  items: SettingsNavItem[];
}

interface ProviderKeyStatus {
  provider: 'openai';
  label: string;
  configured: boolean;
  masked_value: string | null;
  validation_status: 'valid' | 'invalid' | 'unknown';
  last_validated_at: string | null;
  last_validation_error: string | null;
  updated_at: string | null;
}

const INVALID_PROVIDER_BADGE_PATHS = new Set([
  '/v3/settings/billing/provider-keys',
  '/v3/settings/billing/usage',
]);

const PROVIDER_KEY_STATUS_EVENT = 'billing-provider-key-status-updated';

const V3SettingsLayout: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const [providerKeyStatus, setProviderKeyStatus] = useState<ProviderKeyStatus | null>(null);

  useEffect(() => {
    if (!isAdmin) {
      setProviderKeyStatus(null);
      return;
    }

    let cancelled = false;
    const loadProviderKeyStatus = async () => {
      try {
        const response = (await fetchWithAuth('/v1/billing/provider-keys')) as ProviderKeyStatus[];
        const openaiStatus =
          response.find((item) => item.provider === 'openai') ?? null;
        if (!cancelled) {
          setProviderKeyStatus(openaiStatus);
        }
      } catch (error) {
        console.error('Failed to load provider key status for settings layout', error);
      }
    };

    const handleProviderStatusUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<ProviderKeyStatus | null>;
      setProviderKeyStatus(customEvent.detail ?? null);
    };

    void loadProviderKeyStatus();
    window.addEventListener(PROVIDER_KEY_STATUS_EVENT, handleProviderStatusUpdate);

    return () => {
      cancelled = true;
      window.removeEventListener(PROVIDER_KEY_STATUS_EVENT, handleProviderStatusUpdate);
    };
  }, [isAdmin]);

  const hasInvalidProviderKey =
    providerKeyStatus?.configured === true && providerKeyStatus?.validation_status === 'invalid';

  const settingsSections: SettingsNavSection[] = useMemo(
    () =>
      [
        {
          title: 'Settings',
          items: [
            { name: 'Features and Visibility', path: '/v3/settings/features-visibility', adminOnly: true },
            { name: 'Display Settings', path: '/v3/settings/display-settings', adminOnly: true },
            { name: 'Task Queue', path: '/v3/settings/task-queue', adminOnly: true },
            { name: 'Scheduled Jobs', path: '/v3/settings/scheduled-jobs', adminOnly: true },
            { name: 'Monitor Runs', path: '/v3/settings/import-jobs', adminOnly: true },
          ],
        },
        {
          title: 'Organization',
          items: [
            { name: 'Details', path: '/v3/settings/organization', adminOnly: true },
            { name: 'Users & Permissions', path: '/v3/settings/users', adminOnly: true },
            { name: 'API Keys', path: '/v3/settings/api-keys' },
            { name: 'Notifications', path: '/v3/settings/notifications' },
          ],
        },
        {
          title: 'Billing',
          items: [
            { name: 'Provider Keys', path: '/v3/settings/billing/provider-keys', adminOnly: true },
            { name: 'Usage Analytics', path: '/v3/settings/billing/usage', adminOnly: true },
          ],
        },
        {
          title: 'Data',
          items: [
            { name: 'Connectors', path: '/v3/settings/connectors' },
            { name: 'Import History', path: '/v3/settings/imports', adminOnly: true },
          ],
        },
        {
          title: 'Intelligence',
          items: [
            { name: 'Skills', path: '/v3/settings/skills' },
            { name: 'FOS Corpus', path: '/v3/settings/fos', adminOnly: true },
            { name: 'Ombudsman Decisions', path: '/v3/settings/ombudsman', adminOnly: true },
            { name: 'Taxonomy', path: '/v3/settings/taxonomy', adminOnly: true },
          ],
        },
      ]
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => isAdmin || !item.adminOnly),
        }))
        .filter((section) => section.items.length > 0),
    [isAdmin],
  );

  return (
    <div className="flex h-full flex-col bg-[var(--go-spring)] px-4 py-4 md:px-6 md:py-5 xl:px-8 xl:py-6">
      <div className="mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Settings
        </p>
        <h1 className="mt-1 font-jakarta text-2xl font-extrabold tracking-[-0.02em] text-foreground">
          Workspace configuration
        </h1>
        <p className="mt-2 max-w-2xl text-[13px] text-muted-foreground">
          Manage organization defaults, data connections, intelligence sources, and access controls.
        </p>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-5 lg:flex-row">
        <aside className="w-full shrink-0 rounded-[24px] border border-border bg-background shadow-[var(--shadow-card)] lg:w-72">
          <nav className="flex max-h-full flex-col gap-6 overflow-y-auto p-4">
            {settingsSections.map((section) => (
              <div key={section.title}>
                <div className="mb-2 px-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    {section.title}
                  </span>
                </div>
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const isActive = location.pathname === item.path;

                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className={
                          isActive
                            ? 'flex items-center rounded-2xl border border-primary/15 bg-[var(--go-good-vibes-tint)] px-3 py-2.5 text-[13px] font-semibold text-primary shadow-[var(--shadow-card)]'
                            : 'flex items-center rounded-2xl border border-transparent px-3 py-2.5 text-[13px] font-medium text-muted-foreground transition-[background-color,color,border-color,box-shadow] hover:border-primary/10 hover:bg-accent hover:text-foreground'
                        }
                      >
                        <span>{item.name}</span>
                        {hasInvalidProviderKey && INVALID_PROVIDER_BADGE_PATHS.has(item.path) ? (
                          <Badge
                            variant="attention"
                            className="ml-auto border-0 px-2 py-0.5 text-[10px] uppercase tracking-[0.08em]"
                          >
                            Alert
                          </Badge>
                        ) : null}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        <div className="min-h-0 min-w-0 flex-1 overflow-hidden rounded-[28px] border border-border bg-background shadow-[var(--shadow-card)]">
          <div className="h-full overflow-auto">
            {hasInvalidProviderKey ? (
              <div className="border-b border-border/70 px-4 py-4 md:px-6">
                <Alert className="border-rose-200 bg-rose-50 text-rose-900">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Organization OpenAI key needs attention</AlertTitle>
                  <AlertDescription className="text-rose-800">
                    <p>
                      Organization-scoped LLM calls will fail until the stored provider key is
                      fixed or removed.
                    </p>
                    {providerKeyStatus?.last_validation_error ? (
                      <p>{providerKeyStatus.last_validation_error}</p>
                    ) : null}
                  </AlertDescription>
                </Alert>
              </div>
            ) : null}
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default V3SettingsLayout;
